import { CheckIcon, FileSpreadsheet, ListFilter, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { FilePdfIcon, FilePowerpointIcon, FileWordIcon, MailAttachmentIcon } from '@/components/ui/icons';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface SimpleTableFacetedFilterOption {
  label: string;
  value: string;
}

export const FILE_TYPE_FACET_OPTIONS: SimpleTableFacetedFilterOption[] = [
  { value: 'PDF', label: 'PDF' },
  { value: 'Word', label: 'Word' },
  { value: 'PowerPoint', label: 'PowerPoint' },
  { value: 'Excel', label: 'Excel' },
  { value: 'Email', label: 'Email' },
  { value: 'Email with attachments', label: 'Email with attachments' },
  { value: 'Other', label: 'Other' },
];

export const fileTypeFacetOptionsForValues = (values: Iterable<string>) => {
  const availableValues = new Set(values);
  return FILE_TYPE_FACET_OPTIONS.filter((option) => availableValues.has(option.value));
};

interface SimpleTableFacetedFilterProps {
  title: string;
  options: SimpleTableFacetedFilterOption[];
  selectedValues: string[];
  onSelectedValuesChange: (values: string[]) => void;
}

export function SimpleTableFacetedFilter({
  title,
  options,
  selectedValues,
  onSelectedValuesChange,
}: SimpleTableFacetedFilterProps) {
  const selected = new Set(selectedValues);

  return (
    <Popover modal={true}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative h-8 w-6">
          <ListFilter className="h-4 w-4" />
          {selected.size > 0 ? (
            <span className="absolute -right-1 top-0 text-sm text-black/50">{selected.size}</span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[270px] p-0" align="start">
        <Command>
          <CommandInput placeholder={title} />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup className="max-h-64 overflow-auto custom-scrollbar">
              {options.map((option) => {
                const isSelected = selected.has(option.value);

                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      const nextSelected = new Set(selected);
                      if (isSelected) {
                        nextSelected.delete(option.value);
                      } else {
                        nextSelected.add(option.value);
                      }
                      onSelectedValuesChange(Array.from(nextSelected));
                    }}
                    onPointerDown={(event) => event.preventDefault()}
                  >
                    <div
                      className={cn(
                        'mr-2 flex h-4 w-4 items-center justify-center rounded-xs border border-primary',
                        isSelected
                          ? 'bg-primary text-primary-foreground'
                          : 'opacity-50 [&_svg]:invisible',
                      )}
                    >
                      <CheckIcon className="h-4 w-4" />
                    </div>
                    <div className="flex items-center">
                      {title === 'File type' && option.value === 'PDF' ? (
                        <FilePdfIcon className="mr-2 h-4 w-4 text-red-600" />
                      ) : null}
                      {title === 'File type' && option.value === 'Word' ? (
                        <FileWordIcon className="mr-2 h-4 w-4 text-blue-700" />
                      ) : null}
                      {title === 'File type' && option.value === 'PowerPoint' ? (
                        <FilePowerpointIcon className="mr-2 h-4 w-4 text-orange-700" />
                      ) : null}
                      {title === 'File type' && option.value === 'Excel' ? (
                        <FileSpreadsheet className="mr-2 h-4 w-4 text-emerald-700" />
                      ) : null}
                      {title === 'File type' && option.value === 'Email' ? (
                        <Mail className="mr-2 h-4 w-4 text-gray-500" />
                      ) : null}
                      {title === 'File type' && option.value === 'Email with attachments' ? (
                        <div className="mr-2">
                          <MailAttachmentIcon className="h-4 w-4 text-gray-500" />
                        </div>
                      ) : null}
                      <span>{option.label}</span>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {selected.size > 0 ? (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => onSelectedValuesChange([])}
                    onPointerDown={(event) => event.preventDefault()}
                    className="justify-center text-center"
                  >
                    Clear filters
                  </CommandItem>
                </CommandGroup>
              </>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
